import styled, { keyframes } from 'styled-components'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { CSSTransition } from 'react-transition-group-v4'

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`
const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`
const Wrap = styled.div`
  @media screen and (prefers-reduced-motion: no-preference) {
    &.fade-appear,
    &.fade-enter {
      opacity: 0;
    }

    &.fade-enter-active,
    &.fade-appear-active {
      animation: ${fadeIn} ${({ timeout }) => `${timeout?.enter ?? timeout}`}ms ease-in;
    }

    &.fade-appear-done,
    &.fade-enter-done {
      opacity: 1;
    }

    &.fade-exit-active {
      animation: ${fadeOut} ${({ timeout }) => `${timeout?.exit ?? timeout}ms`} ease-out;
    }

    &.fade-exit-done {
      opacity: 0;
    }
  }
`
/** The Fade component allows animating the opacity of its children. */
const Fade = ({
  children,
  in: inProp,
  timeout = 300,
  mountOnEnter = true,
  unmountOnExit = false,
  appear = false,
  ...rest
}) => {
  const [show, setShow] = useState(inProp)

  useEffect(() => {
    setShow(inProp)
  }, [inProp])

  return (
    <CSSTransition
      in={show}
      timeout={timeout}
      classNames="fade"
      mountOnEnter={mountOnEnter}
      unmountOnExit={unmountOnExit}
      appear={appear}>
      <Wrap timeout={timeout} {...rest}>
        {children}
      </Wrap>
    </CSSTransition>
  )
}

Fade.propTypes = {
  /** Whether to show the children component or not and trigger enter/exit states */
  in: PropTypes.bool,
  /** Whether the children should perform the enter transition on first mount */
  appear: PropTypes.bool,
  /** Whether the children should be lazily mounted the first time `in` is true */
  mountOnEnter: PropTypes.bool,
  /** Whether the children should be unmounted when the exit state is reached */
  unmountOnExit: PropTypes.bool,
  /** The duration of the transition in ms */
  timeout: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      appear: PropTypes.number,
      enter: PropTypes.number,
      exit: PropTypes.number
    })
  ]).isRequired
}

export { Fade }
